import React from 'react';
import { useCurrentUser } from '@qualio/ui-components';
import { HeaderEditButton } from './HeaderEditButton';
import { HeaderSendForApprovalButton } from './HeaderSendForApprovalButton';
import { HeaderSendForReviewButton } from './HeaderSendForReviewButton';
import { HeaderRevertToDraftButton } from './HeaderRevertToDraftButton';
import { HeaderButtonGroupKebabMenu } from './HeaderButtonGroupKebabMenu';
import { HeaderCancelButton } from './HeaderCancelButton';
import { HeaderSaveChangesButton } from './HeaderSaveChanges';
import { Form } from '../../../../types';
import { useEventDetails, useFormRevertToDraft, useFormStatusChange } from '../../../../hooks';
import { useEditorUpload } from '../../../../context';
import { useFlags } from '../../../../external/LaunchDarklyAdapter';

type HeaderButtonGroupProps = {
  data: Form;
  eventId: number;
  isEditMode: boolean;
  saveForm: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  isUpdateFormLoading: boolean;
  isNoQualityApprover: boolean;
};

export const HeaderButtonGroup: React.FC<HeaderButtonGroupProps> = ({
  data,
  eventId,
  isEditMode = false,
  saveForm,
  isUpdateFormLoading,
  isNoQualityApprover = false,
}) => {
  const { userId, userGroups } = useCurrentUser();
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const isEditorUploading = useEditorUpload()?.isUploading ?? false;
  const { data: event, isLoading } = useEventDetails(eventId);
  const { mutate: formSendForReviewMutate, isLoading: isFormSendForReviewLoading } = useFormStatusChange(
    data.title,
    'for_review',
  );
  const { mutate: formSendForApprovalMutate, isLoading: isFormSendForApprovalLoading } = useFormStatusChange(
    data.title,
    'for_approval',
    isNoQualityApprover,
  );
  const { mutate: formRevertToDraftMutate, isLoading: isFormRevertToDraftLoading } = useFormRevertToDraft(data.title);

  const isEventOpen = event?.issue.status === 'open';
  const isFormOwner = data.owner_id === userId;
  const isEventOwner = event?.issue.owner_id === userId;
  const isEventCollaborator = event?.issue.collaborators.some((collaborator) => collaborator.id === userId) ?? false;
  const isQuality = userGroups.includes('quality');
  const shouldShowActionButton = isEventOpen && isFormOwner && !isEditMode;

  const shouldShowKebabMenu =
    isEventOpen && (isFormOwner || isQuality || isEventOwner || (isEventCollaboratorsEnabled && isEventCollaborator));

  if (isLoading) {
    return null;
  }

  return (
    <>
      <HeaderCancelButton
        eventId={eventId}
        stepId={data.issue_step_id}
        formId={data.id}
        shouldShowEditViewButton={isEditMode}
        isDisabled={isUpdateFormLoading}
      />
      <HeaderSaveChangesButton
        shouldShowEditViewButton={isEditMode}
        isDisabled={isUpdateFormLoading || isEditorUploading}
        isLoading={isUpdateFormLoading}
        saveForm={saveForm}
      />
      <HeaderEditButton
        data={data}
        shouldShowActionButton={shouldShowActionButton}
        isDisabled={isFormSendForApprovalLoading || isFormSendForReviewLoading}
      />
      <HeaderSendForReviewButton
        data={data}
        shouldShowActionButton={shouldShowActionButton}
        isApiLoading={isFormSendForReviewLoading}
        isDisabled={isFormSendForApprovalLoading || isFormRevertToDraftLoading}
        callback={formSendForReviewMutate}
      />
      <HeaderRevertToDraftButton
        data={data}
        shouldShowActionButton={shouldShowActionButton}
        isApiLoading={isFormRevertToDraftLoading}
        isDisabled={isFormSendForReviewLoading || isFormSendForApprovalLoading}
        callback={formRevertToDraftMutate}
      />
      <HeaderSendForApprovalButton
        data={data}
        shouldShowActionButton={shouldShowActionButton}
        isApiLoading={isFormSendForApprovalLoading}
        isDisabled={isFormSendForReviewLoading || isFormRevertToDraftLoading}
        callback={formSendForApprovalMutate}
      />
      <HeaderButtonGroupKebabMenu
        data={data}
        shouldShowKebabMenu={shouldShowKebabMenu}
        isEditMode={isEditMode}
        isDisabled={isFormSendForReviewLoading || isFormSendForApprovalLoading || isFormRevertToDraftLoading}
      />
    </>
  );
};
